/**
 * Snippets Store Types
 *
 * Defines the shape of the centralized snippets state for managing snippets data.
 * The `isLoaded` flag is crucial for preventing duplicate fetch requests -
 * once snippets are loaded, subsequent calls to fetchSnippets() will return
 * the cached data immediately.
 *
 * Site configuration:
 * - `isSnippetSharingForSiteEnabled`: Set once on first fetch from site config; used for Access field and refetches.
 *
 * Error state:
 * - `fetchError`: Set when fetching snippets fails
 */
import type { Snippet } from '../types/snippet.ts';

export type SnippetsState = {
	/** Array of all snippets loaded from the server */
	snippets: Snippet[];
	/** True while a fetch or create operation is in progress */
	isLoading: boolean;
	/** Stores the most recent fetch error, null if no error */
	fetchError: Error | null;
	/** True once snippets have been successfully loaded; prevents redundant fetches */
	isLoaded: boolean;
	/** Site config: true when snippet sharing for site is enabled. Set once on first fetch; used for Access field and refetches. */
	isSnippetSharingForSiteEnabled?: boolean;
};

export const getInitialState = (): SnippetsState => ({
	snippets: [],
	isLoading: false,
	fetchError: null,
	isLoaded: false,
});
